/**
 * Runtime App Config — loaded once after login, used by formatters across the app.
 * Billing formatMoney and shared date helpers read from here.
 */

const DEFAULTS = {
  DATE_FORMAT: 'MM/DD/YYYY',
  AMOUNT_FORMAT: '#,##0.00',
  CURRENCY_CODE: 'USD',
  QTY_FORMAT: '#,##0.###',
  FILE_STORAGE_BUCKET: '',
  FEATURE_STATUS_UPDATES: 'true',
  FEATURE_APPROVAL_FLOW: 'true',
};

let runtime = { ...DEFAULTS };
let listeners = new Set();

export function getAppConfigRuntime() {
  return { ...runtime };
}

export function setAppConfigRuntime(partial) {
  runtime = { ...runtime, ...partial };
  listeners.forEach((fn) => {
    try {
      fn(getAppConfigRuntime());
    } catch {
      /* ignore */
    }
  });
}

/** Apply rows from GET /synctrack/app-config (effective list). */
export function applyAppConfigRows(rows) {
  const next = { ...DEFAULTS };
  (rows || []).forEach((r) => {
    const key = r.configKey || r.config_key;
    const val = r.value1;
    if (key && val != null) {
      next[key] = String(val);
    }
  });
  setAppConfigRuntime(next);
}

export function subscribeAppConfig(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getAppCurrency() {
  return runtime.CURRENCY_CODE || 'USD';
}

export function getAppDateFormat() {
  return runtime.DATE_FORMAT || 'MM/DD/YYYY';
}

export function isFeatureEnabled(key) {
  return String(runtime[key] ?? 'true').toLowerCase() !== 'false';
}

function pad(n) {
  return String(n).padStart(2, '0');
}

function parseDate(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const s = String(value).trim();
  // Date-only YYYY-MM-DD (avoid UTC shift) — only when no time component
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Format a date using App Config DATE_FORMAT. */
export function formatAppDate(value) {
  const d = parseDate(value);
  if (!d) return value == null || value === '' ? '—' : String(value);
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const fmt = getAppDateFormat();
  if (fmt === 'DD/MM/YYYY') return `${dd}/${mm}/${yyyy}`;
  if (fmt === 'YYYY-MM-DD') return `${yyyy}-${mm}-${dd}`;
  return `${mm}/${dd}/${yyyy}`;
}

/** Date + local time (Sync Flow run grids, etc.). */
export function formatAppDateTime(value) {
  const d = parseDate(value);
  if (!d) return value == null || value === '' ? '—' : String(value);
  const datePart = formatAppDate(d);
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  const ss = pad(d.getSeconds());
  return `${datePart} ${hh}:${mi}:${ss}`;
}

function fractionDigitsFromPattern(pattern, fallback) {
  const p = String(pattern || '');
  const i = p.lastIndexOf('.');
  if (i < 0) return 0;
  const frac = p.slice(i + 1).replace(/[^0#]/g, '');
  if (!frac) return fallback;
  // count of 0 = minimum; length = maximum for # placeholders
  return Math.min(6, Math.max(frac.length, fallback));
}

/** Format money using App Config currency (if not passed) and amount fraction digits. */
export function formatAppMoney(value, currency) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  const cur = currency || getAppCurrency() || 'USD';
  const digits = fractionDigitsFromPattern(runtime.AMOUNT_FORMAT, 2);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: cur,
      minimumFractionDigits: Math.min(digits, 2),
      maximumFractionDigits: digits,
    }).format(n);
  } catch {
    return `${cur} ${n.toFixed(digits)}`;
  }
}

/** Format quantity using App Config QTY_FORMAT. */
export function formatAppQuantity(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  const digits = fractionDigitsFromPattern(runtime.QTY_FORMAT, 3);
  try {
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: digits,
    }).format(n);
  } catch {
    return String(n);
  }
}
